import React, { useState, useEffect } from 'react';
import { Col, Row, Form, Card } from 'react-bootstrap';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import DatePicker from '../../components/base/DatePicker';
import { FallingLines } from 'react-loader-spinner';
import { useMainLayoutContext } from '../../providers/MainLayoutProvider';
import { useAppContext } from '../../providers/AppProvider';
import ActivityTimeline from '../../components/timelines/ActivityTimeline';
import LiveTrendChart from '../../components/charts/machine-deep-dive/LiveTrendChart';
import StateTimelineChart from '../../components/charts/machine-deep-dive/StateTimelineChart';
import AlarmDensityHeatmap from '../../components/charts/machine-deep-dive/AlarmDensityHeatmap';
import { INFLUX_API } from '../../config/api';

dayjs.extend(utc);

// === HELPERS ===
const CHART_COLORS = [
  '#00e5ff',
  '#ff5722',
  '#4caf50',
  '#ffeb3b',
  '#e91e63',
  '#9c27b0',
  '#03a9f4'
];
const formatLabel = key =>
  key
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// dbAddress: "DB10,X0.0" | "DB3,REAL32" | "DB3,INT42" | "DB3,BYTE30"
const inferDataTypeFromAddress = address => {
  if (!address) return 'unknown';
  const parts = address.split(',');
  if (parts.length < 2) return 'unknown';

  const typePart = parts[1].trim();
  if (typePart.startsWith('X')) return 'bool';
  if (typePart.startsWith('REAL')) return 'number';
  if (typePart.startsWith('INT')) return 'number';
  if (typePart.startsWith('BYTE')) return 'byte';
  return 'unknown';
};

const inferRole = fieldName => {
  if (
    fieldName.includes('METER') ||
    fieldName.includes('TOTAL') ||
    fieldName.includes('ENERGY')
  )
    return 'counter';
  return 'instant';
};

const getFieldMeta = (machine, field) => {
  const metadata = machine.metadata || {};
  const vars = machine.vars || {};
  const category = metadata[field];
  const address = vars[field];
  const dataType = inferDataTypeFromAddress(address);
  const role = inferRole(field);
  return { field, category, address, dataType, role };
};

// === OPERATIONS PER TYPE ===
const allowedOperations = {
  number: ['mean', 'min', 'max', 'sum']
};

const VIRTUAL_FIELDS = {
  STATUS: ['l75', 'l120', 'ach300', 'dtb1250', 'l120', 'l75'],
  MODE: ['l75', 'l120']
};

// === COMPONENT ===

const MachineDeepDive = () => {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldOperations, setFieldOperations] = useState({});
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'hour'));
  const [toDate, setToDate] = useState(dayjs());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // ALARMS
  const [reports, setReports] = useState([]);
  const [machineStateData, setMachineStateData] = useState({});

  const { setContentClass } = useMainLayoutContext();
  const {
    config: { theme }
  } = useAppContext();

  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const res = await axios.get(
          `${INFLUX_API}/api/machines-config`
        );
        console.log(res);
        setMachines(res.data.machines);
        setSelectedMachine(res.data.machines[0]);
      } catch (err) {
        console.error('❌ Error loading machines config:', err);
      }
    };
    fetchMachines();
  }, []);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, []);

  const extractFields = () => {
    if (!selectedMachine) return { alarms: [], parameters: [] };
    const metadata = selectedMachine.metadata || {};
    const alarms = [];
    const parameters = [];
    Object.entries(metadata).forEach(([key, type]) => {
      if (type === 'alarm') alarms.push(key);
      if (type === 'parameter' || type === 'live') parameters.push(key);
    });
    const virtuals = [];

    if (VIRTUAL_FIELDS.STATUS.includes(selectedMachine.name)) {
      virtuals.push('STATUS');
    }

    if (VIRTUAL_FIELDS.MODE.includes(selectedMachine.name)) {
      virtuals.push('MODE');
    }

    return { alarms, parameters, virtuals };
  };

  const toggleField = field => {
    setSelectedFields(prev => {
      const exists = prev.includes(field);
      const next = exists ? prev.filter(f => f !== field) : [...prev, field];

      if (!exists) {
        const meta = getFieldMeta(selectedMachine, field);
        if (meta.dataType === 'number' && meta.role === 'instant') {
          setFieldOperations(o => ({ ...o, [field]: 'mean' }));
        }
      } else {
        setFieldOperations(o => {
          const copy = { ...o };
          delete copy[field];
          return copy;
        });
      }
      return next;
    });
  };

  const toggleAll = (fields, checked) => {
    const next = checked ? fields : [];
    setSelectedFields(next);

    if (!checked) {
      setFieldOperations({});
      return;
    }

    const ops = {};
    fields.forEach(field => {
      const meta = getFieldMeta(selectedMachine, field);
      if (meta.dataType === 'number' && meta.role === 'instant') {
        ops[field] = 'mean';
      }
    });

    setFieldOperations(ops);
  };

  const handleOperationChange = (field, op) => {
    setFieldOperations(prev => ({ ...prev, [field]: op }));
  };

  // ===== UTC CONVERSION AICI =====
  const handleLoadData = async () => {
    if (loading) return; // anti double-click
    setError('');

    if (!selectedMachine) return setError('No machine selected.');
    if (!selectedFields.length) return setError('Select at least one field.');
    if (fromDate.isAfter(toDate)) return setError('Start must be before end.');

    setLoading(true);

    try {
      // ================= UTC =================
      const startDateTimeUTC = fromDate.local().utc().format();
      const endDateTimeUTC = toDate.local().utc().format();

      // ================= META =================
      const metaList = selectedFields.map(field =>
        getFieldMeta(selectedMachine, field)
      );

      // ================= SPLIT FIELDS =================

      // ALARMS în Influx (bool -> eveniment)
      const alarms = metaList.filter(m => m.category === 'alarm');

      // STATUS & MODE (virtuale)
      const virtuals = selectedFields.filter(f =>
        ['STATUS', 'MODE'].includes(f)
      );

      // PARAMETRI (bool + number fără discriminare)
      const parameters = metaList
        .filter(m => m.category === 'parameter' || m.category === 'live')
        .map(m => m.field);

      const hasAlarms = alarms.length > 0;
      const hasParams = parameters.length > 0 || virtuals.length > 0;

      if (!hasAlarms && !hasParams) {
        setError('Nothing valid selected.');
        setLoading(false);
        return;
      }

      console.log(
        '🔔 ALARMS:',
        alarms.map(a => a.field)
      );
      console.log('📈 PARAMETERS:', parameters);
      console.log('🧠 VIRTUALS:', virtuals);

      let alarmsResult = null;
      let machineStateResult = null;

      // ==================== ALARMS ====================
      if (hasAlarms) {
        const res = await axios.post(
          `${INFLUX_API}/api/alarms/query`,
          {
            machine: selectedMachine.name,
            from: startDateTimeUTC,
            to: endDateTimeUTC,
            alarms
          }
        );

        alarmsResult = res.data;
      }

      // ==================== PARAMETERS + STATUS + MODE ====================
      if (hasParams) {
        const res = await axios.post(
          `${INFLUX_API}/api/machine-state/query`,
          {
            machine: selectedMachine.name,
            from: startDateTimeUTC,
            to: endDateTimeUTC,
            fields: [...parameters, ...virtuals]
          }
        );

        machineStateResult = res.data;
      }

      // ==================== APPLY RESULTS ====================

      if (alarmsResult) {
        console.log('✅ ALARMS RESPONSE:', alarmsResult);
        setReports(alarmsResult.reports || []);
      }

      if (machineStateResult) {
        console.log('✅ MACHINE STATE RESPONSE:', machineStateResult);
        setMachineStateData(machineStateResult.data || {});
      }

      // ✅ reset UI selections după succes
      setSelectedFields([]);
      setFieldOperations({});
    } catch (err) {
      console.error('❌ API ERROR:', err);
      setError('API error. Check console.');
    } finally {
      setLoading(false);
    }
  };

  const { alarms, parameters, virtuals } = extractFields();

  const allAlarmsSelected =
    alarms.length && alarms.every(a => selectedFields.includes(a));
  const allParamsSelected =
    parameters.length && parameters.every(p => selectedFields.includes(p));

  const numericMachineData = Object.fromEntries(
    Object.entries(machineStateData).filter(
      ([field]) => !['STATUS', 'MODE'].includes(field)
    )
  );

  if (!machines.length) {
    return (
      <div style={styles.spinnerContainer}>
        <FallingLines
          color={theme === 'dark' ? 'white' : '#3874ff'}
          width="100"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.spinnerContainer}>
        <FallingLines
          color={theme === 'dark' ? 'white' : '#3874ff'}
          width="120"
        />
        <div className="mt-3 fw-bold">Loading data from InfluxDB...</div>
      </div>
    );
  }

  return (
    <Row className="g-2 p-3">
      {/* HEADER */}
      <Col md={12}>
        <Card className="mb-2">
          <Card.Body>
            {/* ================= ROW 1 ================= */}
            <Row className="g-2 align-items-end">
              {/* MACHINE */}
              <Col md={3}>
                <Form.Label>Machine</Form.Label>
                <Form.Select
                  value={selectedMachine?.name}
                  onChange={e => {
                    const m = machines.find(x => x.name === e.target.value);
                    setSelectedMachine(m);
                    setSelectedFields([]);
                    setFieldOperations({});
                  }}
                >
                  {machines.map(m => (
                    <option key={m.name}>{m.name}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* START DATE */}
              <Col md={2}>
                <Form.Label>Start Date</Form.Label>
                <DatePicker
                  value={fromDate.toDate()}
                  onChange={([d]) =>
                    setFromDate(prev =>
                      prev
                        .set('year', dayjs(d).year())
                        .set('month', dayjs(d).month())
                        .set('date', dayjs(d).date())
                    )
                  }
                />
              </Col>

              {/* START TIME */}
              <Col md={2}>
                <Form.Label>Start Time</Form.Label>
                <DatePicker
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i'
                  }}
                  value={fromDate.format('HH:mm')}
                  hideIcon
                  onChange={([t]) =>
                    setFromDate(prev =>
                      prev
                        .set('hour', dayjs(t).hour())
                        .set('minute', dayjs(t).minute())
                    )
                  }
                />
              </Col>

              {/* END DATE */}
              <Col md={2}>
                <Form.Label>End Date</Form.Label>
                <DatePicker
                  value={toDate.toDate()}
                  onChange={([d]) =>
                    setToDate(prev =>
                      prev
                        .set('year', dayjs(d).year())
                        .set('month', dayjs(d).month())
                        .set('date', dayjs(d).date())
                    )
                  }
                />
              </Col>

              {/* END TIME */}
              <Col md={2}>
                <Form.Label>End Time</Form.Label>
                <DatePicker
                  options={{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i'
                  }}
                  value={toDate.format('HH:mm')}
                  hideIcon
                  onChange={([t]) =>
                    setToDate(prev =>
                      prev
                        .set('hour', dayjs(t).hour())
                        .set('minute', dayjs(t).minute())
                    )
                  }
                />
              </Col>

              {/* LOAD */}
              <Col md={1}>
                <button
                  onClick={handleLoadData}
                  className="btn btn-primary w-100"
                  disabled={loading}
                  style={{ height: '38px' }}
                >
                  {loading ? 'LOADING' : 'LOAD'}
                </button>
              </Col>
            </Row>

            {/* ================= ROW 2 (ONLY UNDER MACHINE) ================= */}
            {virtuals.length > 0 && (
              <Row className="mt-2">
                <Col md={3}>
                  <div
                    className="d-flex gap-3 flex-wrap"
                    style={{
                      padding: '6px 8px',
                      background: 'rgba(0,0,0,0.05)',
                      borderRadius: '8px'
                    }}
                  >
                    {virtuals.map(field => (
                      <Form.Check
                        key={field}
                        type="switch"
                        id={`virtual-${field}`}
                        label={field}
                        checked={selectedFields.includes(field)}
                        onChange={() => toggleField(field)}
                        className="fw-bold small"
                      />
                    ))}
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>

      {error && (
        <Col md={12}>
          <div className="alert alert-danger">{error}</div>
        </Col>
      )}

      {/* ================== ALARMS ================== */}
      <Col md={6}>
        <Card>
          <Card.Header className="d-flex justify-content-between">
            Alarms
            <Form.Check
              type="switch"
              label={allAlarmsSelected ? 'Clear' : 'All'}
              checked={allAlarmsSelected}
              onChange={e => toggleAll(alarms, e.target.checked)}
              disabled={loading}
            />
          </Card.Header>
          <Card.Body style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Row>
              {alarms.map(f => (
                <Col md={6} key={f}>
                  <Form.Check
                    label={formatLabel(f)}
                    checked={selectedFields.includes(f)}
                    onChange={() => toggleField(f)}
                    disabled={loading}
                  />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </Col>

      {/* ================= PARAMETERS ================= */}
      <Col md={6}>
        <Card>
          <Card.Header className="d-flex justify-content-between">
            Parameters
            <Form.Check
              type="switch"
              label={allParamsSelected ? 'Clear' : 'All'}
              checked={allParamsSelected}
              onChange={e => toggleAll(parameters, e.target.checked)}
              disabled={loading}
            />
          </Card.Header>
          <Card.Body style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Row>
              {parameters.map(field => {
                const meta = getFieldMeta(selectedMachine, field);
                const canOp =
                  meta.dataType === 'number' && meta.role === 'instant';

                return (
                  <Col md={6} key={field}>
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Check
                        label={formatLabel(field)}
                        checked={selectedFields.includes(field)}
                        onChange={() => toggleField(field)}
                        disabled={loading}
                      />

                      {canOp && selectedFields.includes(field) && (
                        <Form.Select
                          size="sm"
                          style={{ width: '90px' }}
                          value={fieldOperations[field]}
                          onChange={e =>
                            handleOperationChange(field, e.target.value)
                          }
                        >
                          {allowedOperations.number.map(op => (
                            <option key={op}>{op}</option>
                          ))}
                        </Form.Select>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      </Col>
      {/* ================= VIRTUAL STATES ================= */}

      {/* ================= PARAMETERS CHARTS ================= */}
      {machineStateData.STATUS && (
        <Col md={6}>
          <StateTimelineChart
            title="Machine Status"
            type="STATUS"
            data={machineStateData.STATUS}
          />
        </Col>
      )}
      {machineStateData.MODE && (
        <Col md={6}>
          <StateTimelineChart
            title="Machine Mode"
            type="MODE"
            data={machineStateData.MODE}
          />
        </Col>
      )}
      {!loading && Object.keys(numericMachineData).length > 0 && (
        <Col md={12}>
          <Card className="mt-3">
            <Card.Header className="fw-bold">Parameters Trends</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {Object.entries(numericMachineData).map(
                  ([field, data], index) => (
                    <Col md={6} key={field}>
                      <LiveTrendChart
                        title={formatLabel(field)}
                        data={data}
                        color={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    </Col>
                  )
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      )}
      {!loading && reports.length > 0 && (
        <>
          <Col md={6}>
            <Card className="mb-3" style={{ height: 420 }}>
              <Card.Header className="fw-bold">Recent activity</Card.Header>
              <Card.Body style={{ overflowY: 'auto' }}>
                <ActivityTimeline reports={reports} />
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3" style={{ height: 420 }}>
              <Card.Header className="fw-bold">
                Alarm Density (Days X Horuse)
              </Card.Header>
              {/* <Card.Body style={{ overflowY: 'auto' }}> */}
              <AlarmDensityHeatmap alarms={reports} fixedHeight={330} />
              {/* </Card.Body> */}
            </Card>
          </Col>
        </>
      )}
    </Row>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  }
};

export default MachineDeepDive;
